import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { humanizeToolName, localizeStepLabel } from "@orbin/core";

/**
 * Label for a step. The runtime stores it in the account language, so it is shown in the
 * language of this screen; a raw tool name or an older generic English label gets the same
 * label, in Portuguese when no language is active.
 */
export function toolLabel(nameOrLabel: string): string {
  // Before a language is active, a raw tool name reads as plain words.
  if (!i18n.locale)
    return /^[a-z][a-z0-9_]*$/.test(nameOrLabel) ? humanizeToolName(nameOrLabel) : nameOrLabel;
  return localizeStepLabel(nameOrLabel, i18n.locale);
}

const ACTIVITY_LINES: ReadonlyArray<[RegExp, (m: RegExpMatchArray) => string]> = [
  [
    /^Running on your computer: (.*)$/s,
    (m) => i18n._(msg`Running on your computer: ${m[1] ?? ""}`),
  ],
  [/^Running: (.*)$/s, (m) => i18n._(msg`Running: ${m[1] ?? ""}`)],
  [/^Running a script$/, () => i18n._(msg`Running a script`)],
  [/^Running a command$/, () => i18n._(msg`Running a command`)],
  [/^Running a script on your computer$/, () => i18n._(msg`Running a script on your computer`)],
  [/^Running a command on your computer$/, () => i18n._(msg`Running a command on your computer`)],
  [/^Reading (.+) from (.+)$/s, (m) => i18n._(msg`Reading ${m[1] ?? ""} from ${m[2] ?? ""}`)],
  [/^Reading skill: (.*)$/s, (m) => i18n._(msg`Reading skill: ${m[1] ?? ""}`)],
  [/^Reading page: (.*)$/s, (m) => i18n._(msg`Reading page: ${m[1] ?? ""}`)],
  [/^Reading the page$/, () => i18n._(msg`Reading the page`)],
  [/^Reading (.*)$/s, (m) => i18n._(msg`Reading ${m[1] ?? ""}`)],
  [/^Writing (.*)$/s, (m) => i18n._(msg`Writing ${m[1] ?? ""}`)],
  [/^Listing (.*)$/s, (m) => i18n._(msg`Listing ${m[1] ?? ""}`)],
  [/^Attaching (.*)$/s, (m) => i18n._(msg`Attaching ${m[1] ?? ""}`)],
  [/^Opening page: (.*)$/s, (m) => i18n._(msg`Opening page: ${m[1] ?? ""}`)],
  [/^Opening (.*)$/s, (m) => i18n._(msg`Opening ${m[1] ?? ""}`)],
  [/^Searching the web: (.*)$/s, (m) => i18n._(msg`Searching the web: ${m[1] ?? ""}`)],
  [/^Rendering a chart$/, () => i18n._(msg`Rendering a chart`)],
  [/^Looking at the screen$/, () => i18n._(msg`Looking at the screen`)],
  [/^Using the page$/, () => i18n._(msg`Using the page`)],
  [/^Operating the computer$/, () => i18n._(msg`Operating the computer`)],
  [/^Saving a note to memory$/, () => i18n._(msg`Saving a note to memory`)],
  [/^Delegating to helper: (.*)$/s, (m) => i18n._(msg`Delegating to helper: ${m[1] ?? ""}`)],
  [/^Using (.*)$/s, (m) => i18n._(msg`Using ${m[1] ?? ""}`)],
];

/**
 * Localizes a tool status line the runtime streams. Any other line, such as the model's own
 * narration in whatever language it wrote, has no label here and returns undefined.
 */
export function localizeActivityLine(line: string): string | undefined {
  if (!i18n.locale) return undefined;
  for (const [pattern, render] of ACTIVITY_LINES) {
    const match = line.match(pattern);
    if (match) return render(match);
  }
  return undefined;
}
